// src/types/common.ts
function isCuid(str) {
  return /^c[a-z0-9]{24}$/.test(str);
}

// src/constants/models.ts
var DEFAULT_SONNET_MODEL = "claude-sonnet-5";
var DEFAULT_OPUS_MODEL = "claude-opus-4-8";
var DEFAULT_HAIKU_MODEL = "claude-haiku-4-5-20251001";
var FABLE_MODEL = "claude-fable-5";
var PREVIOUS_SONNET_MODEL = "claude-sonnet-4-6";

// src/types/project-settings-types.ts
var PREVIEW_PORT_DENY_LIST = [5432, 6379, 9200];
function isAllowablePreviewPort(port) {
  if (typeof port !== "number" || !Number.isInteger(port)) return false;
  if (port < 1 || port > 65535) return false;
  return !PREVIEW_PORT_DENY_LIST.includes(port);
}
function sanitizeSessionPreviewPorts(ports) {
  if (!Array.isArray(ports)) return [];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const raw of ports) {
    if (!raw || typeof raw !== "object") continue;
    const portValue = raw.port;
    if (!isAllowablePreviewPort(portValue)) continue;
    if (seen.has(portValue)) continue;
    seen.add(portValue);
    const labelValue = raw.label;
    const visValue = raw.visibility;
    out.push({
      port: portValue,
      ...typeof labelValue === "string" ? { label: labelValue } : {},
      ...visValue === "public" || visValue === "private" ? { visibility: visValue } : {}
    });
  }
  return out;
}
function resolveAllowedPreviewPorts(sources) {
  const set = /* @__PURE__ */ new Set();
  if (Array.isArray(sources.sessionPreviewPorts)) {
    for (const entry of sources.sessionPreviewPorts) {
      if (isAllowablePreviewPort(entry?.port)) set.add(entry.port);
    }
  }
  return [...set].sort((a, b) => a - b);
}
var PROJECT_FUNCTION_DEFAULTS = {
  identification: {
    model: DEFAULT_SONNET_MODEL,
    effort: "low",
    thinking: { type: "adaptive" }
  },
  suggestionHandler: {
    model: DEFAULT_SONNET_MODEL,
    effort: "high",
    thinking: { type: "adaptive" }
  },
  prReview: {
    model: DEFAULT_SONNET_MODEL,
    effort: "low",
    thinking: { type: "adaptive" }
  },
  reporterEmail: {
    model: DEFAULT_SONNET_MODEL,
    effort: "high",
    thinking: { type: "adaptive" }
  }
};
function resolveProjectFunctionConfig(settings, functionId) {
  const defaults = PROJECT_FUNCTION_DEFAULTS[functionId];
  const override = settings?.functionSettings?.[functionId];
  return {
    model: override?.model ?? defaults.model,
    effort: override?.effort ?? defaults.effort,
    thinking: override?.thinking ?? defaults.thinking,
    instructions: override?.instructions
  };
}
var MAX_AGENT_FLAVOR_LENGTH = 2e3;
function applyAgentFlavor(instructions, settings) {
  const flavor = settings?.agentFlavor?.trim();
  if (!flavor) return instructions;
  const section = `Flavor:
${flavor}`;
  return instructions?.trim() ? `${instructions}

${section}` : section;
}
function resolveCodeReviewMode(settings) {
  return settings?.codeReviewMode ?? "off";
}
function resolveGcpEnvResources(settings) {
  return settings?.gcpEnvResources ?? {};
}

// src/types/gcp-types.ts
var GCP_ENVS = ["prod", "dev", "claudespace"];

// src/types/coding-agent.ts
var TUI_KINDS = ["claude-code", "opencode"];
var AGENT_PROVIDERS = ["anthropic", "openai"];
var AGENT_KEY_KINDS = ["oauth_token", "api_key", "chatgpt_oauth"];

// src/types/agent-project-types.ts
var TASK_STATUS_ORDER = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];

// src/types/task-types.ts
var CARD_TYPE_VALUES = ["task", "incident", "suggestion", "ephemeral", "chat"];
var UNIFIED_STATUSES = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];
var CARD_TYPE_STATUSES = {
  task: UNIFIED_STATUSES,
  incident: UNIFIED_STATUSES,
  suggestion: UNIFIED_STATUSES,
  // Ephemeral cards (e.g. automated code review) are created past Planning and
  // only ever live in InProgress → Complete/Cancelled, but reuse the unified set
  // so status validation never rejects a shared transition.
  ephemeral: UNIFIED_STATUSES,
  // Chat cards spawn a conversational cloud agent that never opens a PR — they
  // move InProgress → Complete directly. Reuse the unified set so the direct
  // Complete transition validates.
  chat: UNIFIED_STATUSES
};
function validateStatusForType(type, status) {
  return CARD_TYPE_STATUSES[type].includes(status);
}
var ACTIVE_WORK_STATUSES = [
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete"
];
function isActiveWorkStatus(status) {
  return ACTIVE_WORK_STATUSES.includes(status);
}
var CREATED_DESC_STATUSES = ["ReviewLive"];
function isCreatedDescStatus(status) {
  return CREATED_DESC_STATUSES.includes(status);
}

// src/types/file-upload-limits.ts
var MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

// src/types/service-map.ts
var ALLOWED_FILE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/json",
  "text/csv",
  "text/plain",
  "text/markdown",
  "application/pdf"
];
var EMBED_THRESHOLD_IMAGES = 5 * 1024 * 1024;
var EMBED_THRESHOLD_TEXT = 2 * 1024 * 1024;
var EMBEDDABLE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp"
];
var EMBEDDABLE_TEXT_TYPES = [
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json"
];
function isEmbeddableFile(mimeType, fileSize) {
  if (EMBEDDABLE_IMAGE_TYPES.some((t) => mimeType === t)) {
    return fileSize <= EMBED_THRESHOLD_IMAGES;
  }
  if (EMBEDDABLE_TEXT_TYPES.some((t) => mimeType === t)) {
    return fileSize <= EMBED_THRESHOLD_TEXT;
  }
  return false;
}

// src/types/connection-state.ts
var IDLE_HEARTBEAT_MS = 90 * 1e3;
var STRANDED_RUNNER_STATUSES = /* @__PURE__ */ new Set(["stalled_heartbeat", "stranded", "crashed"]);
function deriveConnectionState(inputs, now = Date.now()) {
  const heartbeatAgeMs = inputs.lastHeartbeatAt ? Math.max(0, now - new Date(inputs.lastHeartbeatAt).getTime()) : null;
  if (inputs.deletionRequestedAt) {
    return { state: "pendingDeletion", heartbeatAgeMs };
  }
  const status = inputs.codespaceStatus;
  if (status === "Deleted") {
    return { state: "deleted", heartbeatAgeMs };
  }
  if (status === "Creating" || inputs.podStatus === "Creating") {
    return { state: "creating", heartbeatAgeMs };
  }
  if (inputs.agentRunnerStatus && STRANDED_RUNNER_STATUSES.has(inputs.agentRunnerStatus)) {
    return { state: "stranded", heartbeatAgeMs };
  }
  if (status === "Stopped" || inputs.podStatus === "Stopped" || inputs.podStatus === "Sleeping") {
    return { state: "stopped", heartbeatAgeMs };
  }
  if (status !== "Running" && !inputs.codespaceId && !inputs.podName) {
    return { state: "none", heartbeatAgeMs };
  }
  if (heartbeatAgeMs !== null && heartbeatAgeMs >= IDLE_HEARTBEAT_MS) {
    return { state: "idle", heartbeatAgeMs };
  }
  if (status === "Running") {
    return { state: "active", heartbeatAgeMs };
  }
  return { state: "none", heartbeatAgeMs };
}
function formatHeartbeatAge(ms) {
  if (ms === null) return "never";
  const sec = Math.floor(ms / 1e3);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

// src/types/agent-event-schema.ts
import { z } from "zod";
var TurnEndToolCallSchema = z.object({
  tool: z.string(),
  input: z.string().optional(),
  output: z.string().optional(),
  timestamp: z.string().optional()
}).passthrough();
var KnownAgentEventSchema = z.discriminatedUnion("type", [
  // ── Lifecycle / connection ────────────────────────────────────────────
  z.object({
    type: z.literal("connected"),
    sessionId: z.string(),
    projectId: z.string().optional()
  }).passthrough(),
  // Open-ended context snapshot spread from buildInitializationContext().
  z.object({ type: z.literal("session_manifest") }).passthrough(),
  z.object({
    type: z.literal("agent_runner_status"),
    reason: z.string(),
    attempt: z.number().optional(),
    attempts: z.number().optional()
  }).passthrough(),
  z.object({ type: z.literal("shutdown"), reason: z.string().optional() }).passthrough(),
  z.object({ type: z.literal("mode_changed"), agentMode: z.string() }).passthrough(),
  z.object({ type: z.literal("mode_transition"), from: z.string(), to: z.string() }).passthrough(),
  // ── Turn stream ───────────────────────────────────────────────────────
  z.object({ type: z.literal("message"), content: z.string() }).passthrough(),
  z.object({ type: z.literal("thinking"), message: z.string() }).passthrough(),
  z.object({
    type: z.literal("tool_use"),
    tool: z.string(),
    // Producers send JSON.stringify(input); consumers defend against
    // object inputs from older agents, so the wire stays permissive here.
    input: z.unknown().optional()
  }).passthrough(),
  z.object({
    type: z.literal("tool_result"),
    tool: z.string(),
    output: z.unknown().optional(),
    isError: z.boolean().optional(),
    redactedCount: z.number().optional()
  }).passthrough(),
  z.object({ type: z.literal("turn_end"), toolCalls: z.array(TurnEndToolCallSchema) }).passthrough(),
  z.object({
    type: z.literal("completed"),
    summary: z.string().optional(),
    durationMs: z.number().optional()
  }).passthrough(),
  z.object({ type: z.literal("error"), message: z.string() }).passthrough(),
  z.object({ type: z.literal("agent_typing_start") }).passthrough(),
  z.object({ type: z.literal("agent_typing_stop") }).passthrough(),
  // ── Telemetry ─────────────────────────────────────────────────────────
  // heartbeat/typing: legacy telemetry the server still classifies as
  // transient (TRANSIENT_EVENT_TYPES) — kept in the vocabulary.
  z.object({ type: z.literal("heartbeat") }).passthrough(),
  z.object({ type: z.literal("typing") }).passthrough(),
  z.object({
    type: z.literal("context_update"),
    contextTokens: z.number(),
    contextWindow: z.number(),
    inputTokens: z.number().optional(),
    cacheReadInputTokens: z.number().optional(),
    cacheCreationInputTokens: z.number().optional(),
    totalTokensUsed: z.number().optional()
  }).passthrough(),
  // Two producer shapes share this type: {rateLimitType, utilization, status}
  // (SDK rate_limit_event) and {resetsAt} (agent-connection resume notice).
  z.object({
    type: z.literal("rate_limit_update"),
    rateLimitType: z.string().optional(),
    utilization: z.number().optional(),
    status: z.string().optional(),
    resetsAt: z.string().optional()
  }).passthrough(),
  z.object({
    type: z.literal("context_compacted"),
    trigger: z.string().optional(),
    preTokens: z.number().optional()
  }).passthrough(),
  z.object({
    type: z.literal("tool_progress"),
    toolName: z.string().optional(),
    elapsedSeconds: z.number().optional()
  }).passthrough(),
  z.object({
    type: z.literal("subagent_started"),
    sdkTaskId: z.string().optional(),
    description: z.string().optional()
  }).passthrough(),
  z.object({
    type: z.literal("subagent_progress"),
    sdkTaskId: z.string().optional(),
    description: z.string().optional(),
    toolUses: z.number().optional(),
    durationMs: z.number().optional()
  }).passthrough(),
  // ── Work products ─────────────────────────────────────────────────────
  z.object({ type: z.literal("pr_created"), url: z.string(), number: z.number() }).passthrough(),
  z.object({
    type: z.literal("code_review_complete"),
    result: z.enum(["approved", "changes_requested"]),
    summary: z.string().optional(),
    issues: z.array(
      z.object({
        file: z.string(),
        line: z.number().optional(),
        severity: z.string().optional(),
        description: z.string().optional()
      }).passthrough()
    ).optional()
  }).passthrough(),
  // ── Environment setup / start command ─────────────────────────────────
  z.object({ type: z.literal("setup_output"), stream: z.string(), data: z.string() }).passthrough(),
  z.object({
    type: z.literal("setup_complete"),
    startCommandRunning: z.boolean().optional(),
    // Sanitized server-side by sanitizeSessionPreviewPorts — stays unknown.
    previewPorts: z.unknown().optional()
  }).passthrough(),
  z.object({ type: z.literal("setup_error"), message: z.string() }).passthrough(),
  z.object({ type: z.literal("start_command_started") }).passthrough(),
  z.object({ type: z.literal("start_command_output"), stream: z.string(), data: z.string() }).passthrough(),
  z.object({
    type: z.literal("start_command_exited"),
    code: z.number().nullable().optional(),
    signal: z.string().nullable().optional(),
    message: z.string().optional()
  }).passthrough(),
  z.object({ type: z.literal("start_command_error"), message: z.string() }).passthrough()
]);
var AgentEventSchema = z.union([
  KnownAgentEventSchema,
  z.object({ type: z.string().min(1) }).catchall(z.unknown())
]);

// src/types/agent-session-requests.ts
import { z as z2 } from "zod";
var AgentHeartbeatSchema = z2.object({
  sessionId: z2.string().optional(),
  timestamp: z2.string(),
  status: z2.enum(["active", "idle", "building"]),
  currentAction: z2.string().optional(),
  /** Sender-observed main event-loop lag (ms) — see AgentHeartbeat.loopLagMs. */
  loopLagMs: z2.number().nonnegative().optional()
});
var CreatePRInputSchema = z2.object({
  title: z2.string().min(1),
  body: z2.string(),
  head: z2.string().optional(),
  base: z2.string().optional()
});
var PostToChatInputSchema = z2.object({
  message: z2.string().min(1),
  type: z2.enum(["message", "question", "update"]).optional().default("message")
});
var GetTaskContextRequestSchema = z2.object({
  sessionId: z2.string(),
  includeHistory: z2.boolean().optional().default(false)
});
var GetChatMessagesRequestSchema = z2.object({
  sessionId: z2.string(),
  limit: z2.number().int().positive().optional().default(50),
  offset: z2.number().int().nonnegative().optional().default(0)
});
var GetTaskFilesRequestSchema = z2.object({
  sessionId: z2.string()
});
var GetTaskFileRequestSchema = z2.object({
  sessionId: z2.string(),
  fileId: z2.string()
});
var GetTaskRequestSchema = z2.object({
  sessionId: z2.string(),
  taskSlugOrId: z2.string()
});
var GetCliHistoryRequestSchema = z2.object({
  sessionId: z2.string(),
  limit: z2.number().int().positive().optional().default(100),
  source: z2.enum(["agent", "application"]).optional()
});
var ListSubtasksRequestSchema = z2.object({
  sessionId: z2.string(),
  /** "compact" returns the slim orchestration view (ListSubtasksCompactResponse)
   * with the pack build-slot picture; "full" (default — wire-compat with older
   * agents) returns the verbose SubtaskSummaryDTO[] including description/plan. */
  view: z2.enum(["compact", "full"]).optional()
});
var GetDependenciesRequestSchema = z2.object({
  sessionId: z2.string()
});
var GetSuggestionsRequestSchema = z2.object({
  sessionId: z2.string(),
  status: z2.string().optional(),
  limit: z2.number().int().min(1).max(100).optional()
});
var ListManualTestsRequestSchema = z2.object({
  sessionId: z2.string()
});
var QueryManualTestsRequestSchema = z2.object({
  sessionId: z2.string(),
  cardStatuses: z2.array(z2.string()).optional(),
  testStatuses: z2.array(z2.enum(["open", "approved", "rejected"])).optional()
});
var PostToChatRequestSchema = PostToChatInputSchema;
var CreatePullRequestRequestSchema = CreatePRInputSchema.extend({ sessionId: z2.string() });
var RequestFileUploadRequestSchema = z2.object({
  sessionId: z2.string(),
  fileName: z2.string().min(1).max(255),
  mimeType: z2.string().min(1).max(128),
  fileSize: z2.number().int().positive().max(MAX_FILE_SIZE_BYTES)
});
var ConfirmFileUploadRequestSchema = z2.object({
  sessionId: z2.string(),
  fileId: z2.string(),
  title: z2.string().max(500).optional()
});
var UpdateTaskStatusRequestSchema = z2.object({
  sessionId: z2.string(),
  status: z2.string(),
  force: z2.boolean().optional().default(false)
});
var StoreSessionIdRequestSchema = z2.object({
  sessionId: z2.string(),
  sdkSessionId: z2.string()
});
var SetManualTestsRequestSchema = z2.object({
  sessionId: z2.string(),
  items: z2.array(z2.object({ title: z2.string().min(1) })).min(1)
});
var EditManualTestRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1),
  newTitle: z2.string().min(1)
});
var RemoveManualTestRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1)
});
var ApproveManualTestRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1)
});
var RejectManualTestRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1),
  reason: z2.string().min(1).max(2e3)
});
var HeartbeatRequestSchema = AgentHeartbeatSchema;
var SessionStartRequestSchema = z2.object({
  sessionId: z2.string(),
  agentVersion: z2.string(),
  capabilities: z2.array(z2.string())
});
var SessionStopRequestSchema = z2.object({
  sessionId: z2.string(),
  reason: z2.string().optional()
});
var EndReviewSessionRequestSchema = z2.object({
  sessionId: z2.string(),
  reason: z2.enum(["approved", "changes_requested", "finished"]).optional()
});
var ConnectAgentRequestSchema = z2.object({
  sessionId: z2.string()
});
var ReportAgentStatusRequestSchema = z2.object({
  sessionId: z2.string(),
  status: z2.string(),
  /** Why the agent reports this status (e.g. "user_question" while an AskUserQuestion questionnaire is pending in the TUI). */
  reason: z2.string().optional(),
  /**
   * The pending question text, sent only alongside `reason: "user_question"`
   * so the server can surface it in the user-question notification body (and
   * thus the Attention feed) instead of a generic string. Optional: older
   * agents omit it and the server falls back to the generic wording.
   */
  questionText: z2.string().optional()
});
var NotifyAgentVersionRequestSchema = z2.object({
  sessionId: z2.string(),
  agentVersion: z2.string()
});
var DiscoveredPortSchema = z2.object({
  port: z2.number().int().min(1).max(65535),
  label: z2.string().min(1).max(64).optional(),
  protocol: z2.enum(["http", "tcp"]).optional(),
  detectedAt: z2.string()
});
var ReportDiscoveredPortsRequestSchema = z2.object({
  sessionId: z2.string(),
  ports: z2.array(DiscoveredPortSchema).max(64)
});
var CreateSubtaskRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  storyPointValue: z2.number().int().positive().optional(),
  ordinal: z2.number().int().nonnegative().optional(),
  followParentStatus: z2.boolean().optional(),
  /** Sibling subtask ids or slugs this subtask blocks on (explicit dependency
   * metadata — preferred over encoding order in plan text / ordinal). */
  dependsOn: z2.array(z2.string().min(1)).max(32).optional()
});
var UpdateSubtaskRequestSchema = z2.object({
  sessionId: z2.string(),
  subtaskId: z2.string(),
  title: z2.string().min(1).optional(),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  /** Orchestration statuses only ("Planning" | "Open") — the pack parent's
   * sanctioned promotion path. Execution statuses stay with the build
   * pipeline / force_update_task_status. Enforced server-side. */
  status: z2.string().optional(),
  /** Assign a project agent to the child — accepts the agent's id or exact
   * name; resolved against the parent task's project server-side. */
  agentIdOrName: z2.string().min(1).optional(),
  storyPointValue: z2.number().int().positive().optional(),
  followParentStatus: z2.boolean().optional(),
  /** Replace this subtask's dependency edges with these sibling ids/slugs.
   * Empty array clears all. Omit to leave dependencies unchanged. */
  dependsOn: z2.array(z2.string().min(1)).max(32).optional()
});
var DeleteSubtaskRequestSchema = z2.object({
  sessionId: z2.string(),
  subtaskId: z2.string()
});
var GetTaskPropertiesRequestSchema = z2.object({
  sessionId: z2.string()
});
var UpdateTaskFieldsRequestSchema = z2.object({
  sessionId: z2.string(),
  plan: z2.string().optional(),
  description: z2.string().optional()
});
var UpdateTaskPropertiesRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().optional(),
  storyPointValue: z2.number().int().positive().optional(),
  tagIds: z2.array(z2.string()).optional(),
  tagNames: z2.array(z2.string()).optional(),
  githubPRUrl: z2.string().url().optional(),
  githubBranch: z2.string().optional()
});
var ListIconsRequestSchema = z2.object({
  sessionId: z2.string()
});
var GenerateTaskIconRequestSchema = z2.object({
  sessionId: z2.string(),
  prompt: z2.string().min(1),
  aspectRatio: z2.string().optional()
});
var SearchFaIconsRequestSchema = z2.object({
  sessionId: z2.string(),
  query: z2.string().min(1),
  first: z2.number().int().positive().optional()
});
var PickFaIconRequestSchema = z2.object({
  sessionId: z2.string(),
  fontAwesomeId: z2.string().min(1),
  fontAwesomeStyle: z2.string().optional()
});
var CreateFollowUpTaskRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1),
  description: z2.string().optional(),
  plan: z2.string().optional(),
  storyPointValue: z2.number().int().positive().optional()
});
var AddDependencyRequestSchema = z2.object({
  sessionId: z2.string(),
  dependsOnSlugOrId: z2.string()
});
var RemoveDependencyRequestSchema = z2.object({
  sessionId: z2.string(),
  dependsOnSlugOrId: z2.string()
});
var CreateSuggestionRequestSchema = z2.object({
  sessionId: z2.string(),
  title: z2.string().min(1),
  description: z2.string().optional(),
  tagNames: z2.array(z2.string()).optional()
});
var VoteSuggestionRequestSchema = z2.object({
  sessionId: z2.string(),
  suggestionId: z2.string(),
  value: z2.union([z2.literal(1), z2.literal(-1)])
});
var TriggerIdentificationRequestSchema = z2.object({
  sessionId: z2.string()
});
var SubmitCodeReviewResultRequestSchema = z2.object({
  sessionId: z2.string(),
  approved: z2.boolean(),
  content: z2.string()
});
var CycleCodingAgentKeyRequestSchema = z2.object({
  sessionId: z2.string(),
  rateLimitType: z2.string(),
  resetsAt: z2.string().optional()
});
var StartChildCloudBuildRequestSchema = z2.object({
  sessionId: z2.string(),
  childTaskId: z2.string()
});
var StopChildBuildRequestSchema = z2.object({
  sessionId: z2.string(),
  childTaskId: z2.string()
});
var ApproveAndMergePRRequestSchema = z2.object({
  sessionId: z2.string(),
  childTaskId: z2.string()
});
var PostChildChatMessageRequestSchema = z2.object({
  sessionId: z2.string(),
  childTaskId: z2.string(),
  message: z2.string().min(1)
});
var UpdateChildStatusRequestSchema = z2.object({
  sessionId: z2.string(),
  childTaskId: z2.string(),
  status: z2.string()
});
var GetAgentStatusRequestSchema = z2.object({
  taskId: z2.string()
});
var GetUiCliHistoryRequestSchema = z2.object({
  taskId: z2.string()
});
var GetActivePtySessionRequestSchema = z2.object({
  taskId: z2.string()
});
var ListActivePtySessionsRequestSchema = z2.object({
  taskId: z2.string()
});
var SendSoftStopRequestSchema = z2.object({
  taskId: z2.string()
});
var StopTaskSessionRequestSchema = z2.object({
  taskId: z2.string(),
  sessionId: z2.string()
});
var FlushTaskQueueRequestSchema = z2.object({
  taskId: z2.string(),
  softStop: z2.boolean().optional()
});
var CancelTaskQueuedMessageRequestSchema = z2.object({
  taskId: z2.string(),
  messageId: z2.string()
});
var FlushSingleQueuedMessageRequestSchema = z2.object({
  taskId: z2.string(),
  messageId: z2.string(),
  softStop: z2.boolean().optional()
});
var AnswerAgentQuestionRequestSchema = z2.object({
  taskId: z2.string(),
  requestId: z2.string(),
  answers: z2.record(z2.string(), z2.string())
});
var ClearAgentTodosRequestSchema = z2.object({
  taskId: z2.string()
});
var AgentQuestionOptionSchema = z2.object({
  label: z2.string(),
  description: z2.string(),
  preview: z2.string().optional()
});
var AgentQuestionSchema = z2.object({
  question: z2.string(),
  header: z2.string(),
  options: z2.array(AgentQuestionOptionSchema),
  multiSelect: z2.boolean().optional()
});
var AskUserQuestionRequestSchema = z2.object({
  sessionId: z2.string(),
  question: z2.string().min(1),
  requestId: z2.string().min(1),
  questions: z2.array(AgentQuestionSchema).min(1)
});
var EmitAgentEventRequestSchema = z2.object({
  sessionId: z2.string(),
  events: z2.array(AgentEventSchema).max(500)
});
var RefreshGithubTokenRequestSchema = z2.object({
  sessionId: z2.string()
});
var ReportReviewSpawnFailureRequestSchema = z2.object({
  sessionId: z2.string(),
  reviewSessionId: z2.string(),
  error: z2.string().max(2e3).optional()
});
var SpawnTaskSessionRequestSchema = z2.object({
  taskId: z2.string(),
  kind: z2.enum(["tui", "shell"])
});
var SpawnTaskReviewRequestSchema = z2.object({
  taskId: z2.string()
});
var ReportSessionSpawnFailureRequestSchema = z2.object({
  sessionId: z2.string(),
  spawnedSessionId: z2.string(),
  error: z2.string().max(2e3).optional()
});
var RefreshGithubTokenResponseSchema = z2.object({
  token: z2.string()
});
var PTY_FRAME_MAX_CHARS = 256 * 1024;
var PTY_MAX_DIMENSION = 1e3;
var PtyOutputRequestSchema = z2.object({
  sessionId: z2.string(),
  data: z2.string().max(PTY_FRAME_MAX_CHARS),
  cols: z2.number().int().positive().max(PTY_MAX_DIMENSION).optional(),
  rows: z2.number().int().positive().max(PTY_MAX_DIMENSION).optional()
});
var PtyEndedRequestSchema = z2.object({
  sessionId: z2.string()
});
var PtyInputRequestSchema = z2.object({
  sessionId: z2.string(),
  data: z2.string().max(PTY_FRAME_MAX_CHARS)
});
var PtyResizeRequestSchema = z2.object({
  sessionId: z2.string(),
  cols: z2.number().int().positive().max(PTY_MAX_DIMENSION),
  rows: z2.number().int().positive().max(PTY_MAX_DIMENSION)
});
var PtyAttachRequestSchema = z2.object({
  sessionId: z2.string()
});
var PtyChatEventPayloadSchema = z2.discriminatedUnion("kind", [
  z2.object({
    kind: z2.literal("init"),
    model: z2.string().max(200),
    claudeSessionId: z2.string().max(100).optional()
  }),
  z2.object({ kind: z2.literal("user_text"), text: z2.string().max(16384) }),
  z2.object({ kind: z2.literal("assistant_text"), text: z2.string().max(16384) }),
  z2.object({
    kind: z2.literal("tool_use"),
    name: z2.string().max(200),
    // Compact preview: JSON.stringify(input) truncated agent-side.
    input: z2.string().max(2e3)
  }),
  z2.object({ kind: z2.literal("turn_end") })
]);
var PtyChatEventRequestSchema = z2.object({
  sessionId: z2.string(),
  event: PtyChatEventPayloadSchema
});
var PtyChatAttachRequestSchema = z2.object({
  sessionId: z2.string()
});
var CreatePRResponseSchema = z2.object({
  prNumber: z2.number().int().positive(),
  prUrl: z2.string().url()
});
var PostToChatResponseSchema = z2.object({
  messageId: z2.string()
});
var UpdateTaskStatusResponseSchema = z2.object({
  taskId: z2.string(),
  status: z2.string()
});
var StoreSessionIdResponseSchema = z2.object({
  success: z2.boolean()
});
var HeartbeatResponseSchema = z2.object({
  acknowledged: z2.boolean()
});
var SessionStartResponseSchema = z2.object({
  sessionId: z2.string(),
  startedAt: z2.string()
});
var SessionStopResponseSchema = z2.object({
  sessionId: z2.string(),
  stoppedAt: z2.string()
});
var DeleteSubtaskResponseSchema = z2.object({
  deleted: z2.boolean()
});

// src/types/agent-session-project-requests.ts
import { z as z3 } from "zod";
var ListAccessibleProjectsRequestSchema = z3.object({
  pageSize: z3.number().int().positive().max(100).optional().default(100)
});
var ListProjectTasksRequestSchema = z3.object({
  projectId: z3.string(),
  status: z3.string().optional(),
  assigneeId: z3.string().optional(),
  unassigned: z3.boolean().optional(),
  limit: z3.number().int().positive().optional().default(50)
}).refine((p) => !(p.unassigned && p.assigneeId), {
  message: "Pass either assigneeId or unassigned, not both"
});
var GetProjectTaskRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string()
});
var SearchProjectTasksRequestSchema = z3.object({
  projectId: z3.string(),
  tagNames: z3.array(z3.string()).optional(),
  searchQuery: z3.string().optional(),
  statusFilters: z3.array(z3.string()).optional(),
  // Card types to include. Omitted/empty → defaults to ["task"] in the handler so
  // search doesn't surface incidents/suggestions unless asked. Enum validation lives
  // at the MCP tool layer (mirrors statusFilters).
  typeFilters: z3.array(z3.string()).optional(),
  assigneeId: z3.string().optional(),
  unassigned: z3.boolean().optional(),
  limit: z3.number().int().positive().optional().default(20)
}).refine((p) => !(p.unassigned && p.assigneeId), {
  message: "Pass either assigneeId or unassigned, not both"
});
var ListProjectTagsRequestSchema = z3.object({
  projectId: z3.string()
});
var GetProjectSummaryRequestSchema = z3.object({
  projectId: z3.string()
});
var CreateProjectTaskRequestSchema = z3.object({
  projectId: z3.string(),
  title: z3.string().min(1),
  description: z3.string().optional(),
  plan: z3.string().optional(),
  status: z3.string().optional(),
  requestingUserId: z3.string().optional()
});
var UpdateProjectTaskRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  title: z3.string().optional(),
  description: z3.string().optional(),
  plan: z3.string().optional(),
  status: z3.string().optional(),
  assignedUserId: z3.string().nullish(),
  requestingUserId: z3.string().optional()
});
var PostToProjectTaskChatRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  content: z3.string(),
  requestingUserId: z3.string().optional()
});
var GetProjectTaskCliRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  limit: z3.number().int().positive().optional().default(50),
  source: z3.string().optional()
});
var GetProjectTaskSessionsRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string()
});
var QueryProjectGcpLogsRequestSchema = z3.object({
  projectId: z3.string(),
  env: z3.enum(["prod", "dev", "claudespace"]).optional(),
  severity: z3.enum(["DEBUG", "INFO", "NOTICE", "WARNING", "ERROR", "CRITICAL", "ALERT", "EMERGENCY"]).optional(),
  services: z3.array(z3.string().min(1).max(200)).max(25).optional(),
  sqlInstances: z3.array(z3.string().min(1).max(200)).max(25).optional(),
  allServices: z3.boolean().optional(),
  search: z3.string().max(256).optional(),
  filter: z3.string().max(1e3).optional(),
  startTime: z3.string().optional(),
  endTime: z3.string().optional(),
  limit: z3.number().int().min(1).max(200).optional().default(50),
  pageToken: z3.string().max(4096).optional()
});
var StartProjectBuildRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  requestingUserId: z3.string().optional()
});
var StopProjectBuildRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  requestingUserId: z3.string().optional()
});
var StartProjectWorkspaceRequestSchema = z3.object({
  projectId: z3.string(),
  requestingUserId: z3.string().optional()
});
var StopProjectWorkspaceRequestSchema = z3.object({
  projectId: z3.string(),
  destroy: z3.boolean().optional(),
  requestingUserId: z3.string().optional()
});
var ListMyLiveSessionsRequestSchema = z3.object({
  projectId: z3.string(),
  /** Admin-only: list another member's sessions instead of the caller's. */
  targetUserId: z3.string().optional()
});
var ListProjectSessionGroupsRequestSchema = z3.object({
  projectId: z3.string()
});
var GetProjectAvailableTuisRequestSchema = z3.object({
  projectId: z3.string()
});
var StartAdhocSessionRequestSchema = z3.object({
  projectId: z3.string(),
  label: z3.string().max(200).optional(),
  /** Coding-agent key to launch under — validated pick-time (ownership + TUI availability) in the handler. */
  codingAgentKeyId: z3.string().optional(),
  /**
   * Session role. Constrained: other task-less modes fall through to the pm
   * runner in the pod entrypoint, and "review" would crash without a task.
   */
  mode: z3.enum(["adhoc", "pm"]).optional(),
  /** Base branch to check out (defaults to the project's dev branch). */
  branch: z3.string().max(300).optional(),
  requestingUserId: z3.string().optional()
});
var StopAdhocSessionRequestSchema = z3.object({
  projectId: z3.string(),
  workspaceId: z3.string(),
  destroy: z3.boolean().optional(),
  requestingUserId: z3.string().optional()
});
var ResumeAdhocSessionRequestSchema = z3.object({
  projectId: z3.string(),
  workspaceId: z3.string(),
  requestingUserId: z3.string().optional()
});
var CreateProjectReleaseRequestSchema = z3.object({
  projectId: z3.string(),
  taskIds: z3.array(z3.string()).optional(),
  requestingUserId: z3.string().optional()
});
var ApproveProjectMergePRRequestSchema = z3.object({
  projectId: z3.string(),
  childTaskId: z3.string(),
  requestingUserId: z3.string().optional()
});
var ListProjectSubtasksRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string()
});
var CreateProjectSubtaskRequestSchema = z3.object({
  projectId: z3.string(),
  parentTaskId: z3.string(),
  title: z3.string().min(1),
  description: z3.string().optional(),
  plan: z3.string().optional(),
  ordinal: z3.number().int().nonnegative().optional(),
  storyPointValue: z3.number().int().positive().optional(),
  followParentStatus: z3.boolean().optional(),
  /** Sibling subtask ids or slugs this subtask blocks on (explicit dependency
   * metadata — preferred over encoding order in plan text / ordinal). */
  dependsOn: z3.array(z3.string().min(1)).max(32).optional(),
  requestingUserId: z3.string().optional()
});
var UpdateProjectSubtaskRequestSchema = z3.object({
  projectId: z3.string(),
  subtaskId: z3.string(),
  title: z3.string().optional(),
  description: z3.string().optional(),
  plan: z3.string().optional(),
  status: z3.string().optional(),
  ordinal: z3.number().int().nonnegative().optional(),
  storyPointValue: z3.number().int().positive().optional(),
  followParentStatus: z3.boolean().optional(),
  requestingUserId: z3.string().optional()
});
var DeleteProjectSubtaskRequestSchema = z3.object({
  projectId: z3.string(),
  subtaskId: z3.string(),
  requestingUserId: z3.string().optional()
});
var GetProjectTaskChatRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  limit: z3.number().int().positive().optional().default(20)
});
var AddProjectTaskDependencyRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  dependsOnSlugOrId: z3.string(),
  requestingUserId: z3.string().optional()
});
var RemoveProjectTaskDependencyRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  dependsOnSlugOrId: z3.string(),
  requestingUserId: z3.string().optional()
});
var VoteProjectSuggestionRequestSchema = z3.object({
  projectId: z3.string(),
  suggestionId: z3.string(),
  value: z3.union([z3.literal(1), z3.literal(-1)]),
  requestingUserId: z3.string().optional()
});
var GetProjectTaskDependenciesRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string()
});
var ListProjectTaskFilesRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string()
});
var GetProjectAttachmentRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  fileId: z3.string(),
  /** Byte offset into text content (paging large logs/JSON). Default 0. */
  offset: z3.number().int().nonnegative().optional(),
  /** Max bytes of text content to return from `offset`. Server default applies. */
  maxBytes: z3.number().int().positive().optional()
});
var RequestProjectFileUploadRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  fileName: z3.string().min(1).max(255),
  mimeType: z3.string().min(1).max(128),
  fileSize: z3.number().int().positive().max(MAX_FILE_SIZE_BYTES),
  requestingUserId: z3.string().optional()
});
var ConfirmProjectFileUploadRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  fileId: z3.string(),
  /** When set, the attachment is also posted to the task chat with this text. */
  comment: z3.string().max(2e3).optional(),
  requestingUserId: z3.string().optional()
});
var CreateProjectPullRequestRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  title: z3.string().min(1),
  body: z3.string(),
  head: z3.string().optional(),
  base: z3.string().optional(),
  requestingUserId: z3.string().optional()
});
var ListProjectMembersRequestSchema = z3.object({
  projectId: z3.string()
});
var AddProjectTaskReviewerRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  userId: z3.string(),
  requestingUserId: z3.string().optional()
});
var RemoveProjectTaskReviewerRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  userId: z3.string(),
  requestingUserId: z3.string().optional()
});
var ListProjectManualTestsRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string()
});
var QueryProjectManualTestsRequestSchema = z3.object({
  projectId: z3.string(),
  cardStatuses: z3.array(z3.string()).optional(),
  testStatuses: z3.array(z3.enum(["open", "approved", "rejected"])).optional()
});
var SetProjectManualTestsRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  items: z3.array(z3.object({ title: z3.string().min(1) })).min(1),
  requestingUserId: z3.string().optional()
});
var EditProjectManualTestRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  title: z3.string().min(1),
  newTitle: z3.string().min(1),
  requestingUserId: z3.string().optional()
});
var RemoveProjectManualTestRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  title: z3.string().min(1),
  requestingUserId: z3.string().optional()
});
var ApproveProjectManualTestRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  title: z3.string().min(1),
  requestingUserId: z3.string().optional()
});
var RejectProjectManualTestRequestSchema = z3.object({
  projectId: z3.string(),
  taskId: z3.string(),
  title: z3.string().min(1),
  reason: z3.string().min(1).max(2e3),
  requestingUserId: z3.string().optional()
});
var CreateProjectSuggestionRequestSchema = z3.object({
  projectId: z3.string(),
  title: z3.string().min(1),
  description: z3.string().optional(),
  tagNames: z3.array(z3.string()).optional(),
  requestingUserId: z3.string().optional()
});

// src/types/task-audit-requests.ts
import { z as z4 } from "zod";
var ProjectTagContextPathSchema = z4.object({
  type: z4.enum(["rule", "doc", "file", "folder"]),
  path: z4.string().min(1).max(500),
  label: z4.string().max(100).optional()
});
var hexColor = z4.string().regex(/^#[0-9a-fA-F]{6}$/, "Expected #RRGGBB hex color");
var CreateProjectTagRequestSchema = z4.object({
  projectId: z4.string(),
  name: z4.string().min(1).max(50),
  color: hexColor.optional(),
  description: z4.string().max(500).optional(),
  contextPaths: z4.array(ProjectTagContextPathSchema).max(20).optional(),
  requestingUserId: z4.string().optional()
});
var UpdateProjectTagRequestSchema = z4.object({
  projectId: z4.string(),
  tagId: z4.string(),
  name: z4.string().min(1).max(50).optional(),
  color: hexColor.optional(),
  description: z4.string().max(500).optional(),
  /** Full replacement of the tag's context links when provided. */
  contextPaths: z4.array(ProjectTagContextPathSchema).max(20).optional(),
  requestingUserId: z4.string().optional()
});
var PostToProjectChatRequestSchema = z4.object({
  projectId: z4.string(),
  content: z4.string().min(1).max(2e4),
  requestingUserId: z4.string().optional()
});
var StartTagAuditRequestSchema = z4.object({
  projectId: z4.string(),
  requestingUserId: z4.string().optional()
});
var StartTaskAuditRequestSchema = z4.object({
  projectId: z4.string(),
  taskIds: z4.array(z4.string()).min(1).max(20),
  requestingUserId: z4.string().optional()
});
var GetActiveAuditSessionsRequestSchema = z4.object({
  projectId: z4.string()
});
var ReportTaskAuditResultRequestSchema = z4.object({
  projectId: z4.string(),
  taskId: z4.string(),
  summary: z4.string(),
  turnGrades: z4.array(
    z4.object({
      turnIndex: z4.number(),
      phase: z4.enum(["planning", "building", "human"]),
      grade: z4.enum(["correct", "neutral", "blunder"]),
      reasoning: z4.string(),
      eventType: z4.string(),
      eventSummary: z4.string()
    })
  ),
  planningAccuracy: z4.number().nullable(),
  buildingAccuracy: z4.number().nullable(),
  humanAccuracy: z4.number().nullable(),
  planningCorrect: z4.number(),
  planningNeutral: z4.number(),
  planningBlunder: z4.number(),
  buildingCorrect: z4.number(),
  buildingNeutral: z4.number(),
  buildingBlunder: z4.number(),
  humanCorrect: z4.number(),
  humanNeutral: z4.number(),
  humanBlunder: z4.number(),
  humanEvaluations: z4.array(
    z4.object({
      messageIndex: z4.number(),
      rating: z4.union([z4.literal(-1), z4.literal(0), z4.literal(1)]),
      reasoning: z4.string()
    })
  ).optional(),
  suggestionIds: z4.array(z4.string()),
  auditCostUsd: z4.number().nullable(),
  model: z4.string().nullable(),
  /** When set, the audit is marked failed with this message instead. */
  error: z4.string().optional()
});
var GetTaskAuditsRequestSchema = z4.object({
  projectId: z4.string(),
  limit: z4.number().int().positive().max(200).optional().default(50)
});
var GetTaskAuditRequestSchema = z4.object({
  projectId: z4.string(),
  auditId: z4.string()
});
var GetTaskAuditAggregatesRequestSchema = z4.object({
  projectId: z4.string()
});
var DeleteTaskAuditRequestSchema = z4.object({
  projectId: z4.string(),
  auditId: z4.string(),
  requestingUserId: z4.string().optional()
});
var MarkInitialPromptSubmittedRequestSchema = z4.object({
  sessionId: z4.string()
});

// src/types/agent-session-events.ts
var CRITICAL_AUTOMATED_SOURCES = /* @__PURE__ */ new Set([
  "ci_failure",
  "review_trigger",
  "merge_conflict",
  "merge_failed",
  "pull_branch",
  // Child-task events for pack parents: the orchestrator must act (merge the
  // child's PR, start unblocked siblings, finish the pack) even after it
  // reported completed for a prior turn. Only ever sent to parent tasks.
  "parent"
]);
var createAgentSessionRoom = (sessionId) => `agentSession:${sessionId}`;
var createProjectRoom = (projectId) => `project:${projectId}`;

// src/types/workspace-v3.ts
var WORKSPACE_DESIRED_STATES = ["Running", "Sleeping", "Destroyed"];
var WORKSPACE_OBSERVED_STATES = [
  "Pending",
  "Provisioning",
  "Running",
  "Sleeping",
  "Destroying",
  "Destroyed",
  "Failed"
];
var WORKSPACE_PURPOSES = ["primary", "review", "project", "adhoc"];
var TASK_LESS_WORKSPACE_PURPOSES = [
  "project",
  "adhoc"
];
var WORKSPACE_POD_PHASES = [
  "Creating",
  "Ready",
  "Bound",
  "Terminating",
  "Gone",
  "Failed"
];
var WORKSPACE_SESSION_ROLES = ["writer", "reader"];
var WORKSPACE_SESSION_MODES = [
  "build",
  "review",
  "pm",
  "help",
  "adhoc",
  "pack",
  "shell"
];
var WORKSPACE_SESSION_STATUSES = ["Pending", "Active", "Stranded", "Ended"];
var MAX_DISCOVERED_PORTS = 16;
function sanitizeDiscoveredPorts(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const entry of raw) {
    if (out.length >= MAX_DISCOVERED_PORTS) break;
    if (!entry || typeof entry !== "object") continue;
    const port = entry.port;
    if (!isAllowablePreviewPort(port)) continue;
    if (seen.has(port)) continue;
    seen.add(port);
    const label = entry.label;
    const protocol = entry.protocol;
    const detectedAt = entry.detectedAt;
    out.push({
      port,
      ...typeof label === "string" && label.length > 0 ? { label } : {},
      ...protocol === "http" || protocol === "tcp" ? { protocol } : {},
      detectedAt: typeof detectedAt === "string" ? detectedAt : (/* @__PURE__ */ new Date(0)).toISOString()
    });
  }
  return out;
}

// src/types/deployment-service-types.ts
var DEPLOYMENT_TARGET_PROVIDERS = [
  "pm2-local",
  "github-actions",
  "vercel",
  "cloud-run",
  "gke"
];
var DEPLOYMENT_TARGET_ENVIRONMENTS = ["dev", "prod", "staging", "custom"];

// src/generated/mcp-tool-registry.generated.ts
var MCP_TOOL_REGISTRY = [
  {
    name: "upload_attachment",
    description: "Upload an image file (e.g. a Playwright screenshot) as a task attachment AND post it to the task chat in one step \u2014 no follow-up post_to_chat call needed. Supports png/jpg/gif/webp.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/attachment-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_manual_test",
    description: "Sign off on (approve) a manual test step on behalf of your authenticated user. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "edit_manual_test",
    description: "Rename an existing manual test step. Identify the test by its current title (case-insensitive); pass the new title to replace it. Use to correct or refine a recorded manual verification step.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "list_manual_tests",
    description: "List the manual test checklist items for the current task. Use to see what manual verification steps have already been recorded.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "query_manual_tests",
    description: "Query manual tests across many tasks in this project, grouped by task. Filter by card status (ReviewDev, ReviewLive, Complete, ...) and/or test status (open | approved | rejected). Use to answer 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "reject_manual_test",
    description: "Flag an issue with (reject) a manual test step on behalf of your authenticated user, recording the reason. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "remove_manual_test",
    description: "Remove an existing manual test step from the task checklist. Identify the test by its title (case-insensitive). Use to delete a stale or incorrect manual verification step.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "set_manual_tests",
    description: "Add manual test steps to the task checklist. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing this PR.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_code_review",
    description: "Approve the code review and exit. Use when the diff passes all review criteria. Takes only a summary \u2014 for changes, use request_code_changes with a structured issues[] list.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/code-review-tools.ts",
    category: "codeReview",
    modes: "review"
  },
  {
    name: "request_code_changes",
    description: "Request changes during code review and exit. Use when substantive issues must be fixed before merge. Each issue: { file, line?, severity: critical|major|minor, description }.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/code-review-tools.ts",
    category: "codeReview",
    modes: "review"
  },
  {
    name: "get_dependencies",
    description: "Get this task's dependencies and their met/unmet status (met = merged to dev). Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/dependency-suggestion-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "get_suggestions",
    description: "List project suggestions sorted by vote score. Filter by status or cap with limit (default 20). Suggestions are project-level ideas, not tasks \u2014 use get_task for tasks.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/dependency-suggestion-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "update_task_properties",
    description: "Set one or more task properties in a single call. Valid keys: title, storyPointValue, tagNames, githubPRUrl, githubBranch. All are optional \u2014 include only the ones you want to update (at least one). Unknown keys are rejected.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/discovery-tools.ts",
    category: "discovery",
    modes: "discovery, auto"
  },
  {
    name: "add_dependency",
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. For post-task follow-ups use create_follow_up_task instead.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_follow_up_task",
    description: "Create a follow-up task that depends on the current task. Use for out-of-scope work or cleanup that should land after this task merges. For blockers use add_dependency.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_pull_request",
    description: "Create a GitHub PR for this task. Auto-stages, commits (commitMessage or title default), pushes to origin, then opens the PR. Always use this instead of gh CLI or raw git.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_suggestion",
    description: "Suggest a feature, improvement, rule, or idea for the project. Duplicates are deduped and your upvote is recorded. For actionable work on this task open a follow-up task.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "force_update_task_status",
    description: "EMERGENCY ONLY: force-override a task's Kanban status. Use when an automatic transition failed and the task is wedged. Normal flow transitions status automatically.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all modes (emergency override; always exposed)"
  },
  {
    name: "post_to_chat",
    description: "Post a message to the task chat for the team to see. Your turn output is NOT shown in chat, so this is the only way the team sees your status, summaries, and questions. Omit task_id to post to the current task's chat; pass a child's ID to message its chat.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "remove_dependency",
    description: "Remove a previously added dependency from this task. When to use: the dependency was added in error or is no longer relevant. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "vote_suggestion",
    description: "Vote +1 or -1 on a project suggestion. Use to express support or disagreement with a specific suggestion returned by get_suggestions.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_and_merge_pr",
    description: "Approve and merge a child task's PR. Preconditions: child in ReviewPR. Returns { merged }: true = merged (status\u2192ReviewDev); false = automerge queued, wait for ReviewDev.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "create_subtask",
    description: "Create a subtask under the current parent task. Use when breaking a complex parent into smaller pieces during planning. For post-task follow-ups use create_follow_up_task.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "delete_subtask",
    description: "Delete a subtask by id. When to use: a subtask was created in error or is no longer needed. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "list_subtasks",
    description: "List all subtasks under the current parent task. Default compact view returns per child: status, agent, story points, PR number/state, dependencies, and holdsBuildSlot \u2014 plus packSlots (in-flight environments vs the PACK_CHILD_LIMIT cap, and which children hold the slots). Use to coordinate child work; pass verbose:true only when you need full description/plan text (large). For non-child tasks use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "start_child_cloud_build",
    description: "Start a cloud build (codespace) for a child task. Preconditions: child status is `Open`, story points set, and an agent assigned \u2014 satisfy all three with update_subtask (status/agentIdOrName/storyPointValue) first; none happen automatically. A PACK_CHILD_LIMIT error is backpressure, not failure: check list_subtasks packSlots for which children hold the in-flight slots, merge/stop one, then retry.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "stop_child_build",
    description: "Send a graceful stop signal to a running child build's agent. Not a force-kill \u2014 the agent may take a moment to wind down. Stopping a child eventually frees its PACK_CHILD_LIMIT build slot (see list_subtasks packSlots).",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "update_subtask",
    description: "Update an existing subtask's fields (title, description, plan, ordinal, storyPointValue, dependsOn) \u2014 and the sanctioned path to make a child buildable: promote it to status Open, assign its agent (agentIdOrName), and set story points. Setting story points does NOT auto-promote; set status explicitly. For the current task use update_task_plan.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "update_task_plan",
    description: "Save the plan and/or description to the current task. In auto/building mode, save the plan BEFORE writing code and keep it current as the approach evolves \u2014 post it, then build; never pause the build waiting for approval. For children use update_subtask; for title/tags/PR use update_task_properties.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "task mode: discovery, auto only. PM/agent modes: review, auto, discovery, help; building (parent task only)."
  },
  {
    name: "create_suggestion",
    description: "File a project suggestion (idea/improvement for maintainers to review). Duplicates are AI-deduped into an existing suggestion with an upvote. Returns the suggestion id.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "create_tag",
    description: "Create a project tag. Include a crisp description and contextPaths (rule/doc/file/folder links agents auto-load when working on matching tasks). Fails if the name already exists.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "get_project_task",
    description: "Fetch any task in the project by id or slug: title, description, plan, status, and metadata. The audit evidence trail starts here.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "get_project_task_logs",
    description: "Read any project task's persisted agent event stream (message / tool_use / turn_end / error / completed). Turn boundaries are turn_end events. Entries are truncated to ~2KB each; max 500 per call.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "list_tags",
    description: "List this project's tags (id, name, color). Use the ids with update_tag.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "post_to_project_chat",
    description: "Post a markdown message to the PROJECT chat \u2014 use once at the end of an audit for the summary the team reads.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "read_project_task_chat",
    description: "Read any project task's chat messages (newest last). role 'user' rows are HUMAN turns; 'assistant'/'system' rows are agent posts and activity-log entries.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "report_task_audit_result",
    description: "Persist one audited task's grades (call once per task after grading it). Pass error instead to mark the audit failed when the evidence is unusable.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "update_tag",
    description: "Update a tag's name, color, description, or contextPaths. contextPaths is a FULL replacement \u2014 include existing links you want to keep. Verify paths exist in the repo first.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "get_attachment",
    description: "Fetch one task file's content plus metadata by file ID. Call list_task_files first to discover IDs and check sizes \u2014 large binaries may be truncated by the service's size limit.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_current_plan",
    description: "Re-read the current task's plan. Use when the user updated the plan or asked you to re-read it \u2014 otherwise the plan is already in initial context. For task metadata use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_execution_logs",
    description: "Read CLI execution logs \u2014 agent reasoning, tool calls, and setup/dev-server output. Filter via source='agent' or 'application'. For human chat use read_task_chat.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_task",
    description: "Look up any task by slug or ID. Returns JSON with id, slug, title, description, plan, status, branch, githubPRNumber, githubPRUrl, storyPoints. For children use list_subtasks.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "list_task_files",
    description: "List all files attached to this task with metadata. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "read_task_chat",
    description: "Read recent human/user chat messages for a task. Omit task_id for the current task; pass a child ID for a child's chat. For agent logs use get_execution_logs.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_attachment",
    description: "Fetch one task file's content plus metadata by file ID (accepts task id or slug). Pass projectId to target a specific project; otherwise the configured default project is used. Images are returned as viewable image blocks. Large text files (logs, JSON) are returned in pages \u2014 use `offset`/`maxBytes` to read more, or fetch `downloadUrl` for the whole file. Call list_task_files first to discover IDs and sizes.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_task_files",
    description: "List all files attached to a task with metadata (no contents \u2014 fast and small). Pass projectId to target a specific project; otherwise the configured default project is used. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "upload_attachment",
    description: "Upload a local file as a task attachment (any file type, up to 25MB). Pass projectId to target a specific project; otherwise the configured default project is used. The file appears under the task's Files. Pass `comment` to also post it to the task chat in the same step.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_release",
    description: "Create a release for the project \u2014 the same flow as the Release button in the web UI. Pass projectId to target a specific project; otherwise the configured default project is used. Creates a release task with a release/YYYY.MM.N branch and a PR from the dev branch to the default branch. Omit taskIds to release ALL cards currently in Review (Dev); pass a subset to cherry-pick \u2014 a cloud build agent then cherry-picks those changes and resolves conflicts. Fails if a release is already in progress or no cards are in Review (Dev).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "delete_task_environment",
    description: "Delete a task environment, including durable Claudespace state. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_build_status",
    description: "Check codespace and agent status for a task. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "resume_task",
    description: "Resume a sleeping task Claudespace. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "sleep_task",
    description: "Sleep a task Claudespace, stopping compute while preserving durable state. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "start_task",
    description: "Start a cloud build (codespace) for a task. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "stop_task",
    description: "Compatibility alias for the legacy stop path. stop_task now performs the same durable sleep behavior as sleep_task, preserving task Claudespace state while stopping compute. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_manual_test",
    description: "Sign off on (approve) a manual test step on a task on behalf of your authenticated user. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "edit_manual_test",
    description: "Rename an existing manual test step on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its current title (case-insensitive) and pass the new title to replace it.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_manual_tests",
    description: "List the manual test checklist items for a task. Pass projectId to target a specific project; otherwise the configured default project is used. Use to see what manual verification steps have already been recorded.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "query_manual_tests",
    description: "Query manual tests across many tasks in a project, grouped by task. Filter by card status (e.g. ReviewDev, ReviewLive, Complete) and/or test status (open | approved | rejected). Use to answer questions like 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests in ReviewDev/ReviewLive with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "reject_manual_test",
    description: "Flag an issue with (reject) a manual test step on a task on behalf of your authenticated user, recording the reason. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_manual_test",
    description: "Remove an existing manual test step from a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "set_manual_tests",
    description: "Add manual test steps to a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing the task's PR.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_dependency",
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_dependencies",
    description: "Get a task's dependencies and their met/unmet status (met = merged to dev). Pass projectId to target a specific project; otherwise the configured default project is used. Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_dependency",
    description: "Remove a previously added dependency from a task. Pass projectId to target a specific project; otherwise the configured default project is used. The task is no longer blocked by the named task. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "query_gcp_logs",
    description: "Query Google Cloud Logging for a project's linked GCP environments \u2014 use this to investigate production or dev issues directly ('something broke on prod'). Envs: 'prod' and 'dev' are the project's Cloud Run apps + Cloud SQL databases (scoped by default to the resources linked in project settings); 'claudespace' is the project's GKE agent-pod namespace. Start broad (severity=ERROR, sinceMinutes=60), then narrow with services/search. Returns compact lines: '<time> <SEVERITY> [<source>] <message>'. When the response ends with a pageToken line, pass that token back as pageToken for the next page. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/logs.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_project_summary",
    description: "Get overall project status: task counts by status, active builds, repo info. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_project_members",
    description: "List project members with user ID, name, email, and access level \u2014 use to resolve a person's name or email to a user ID for task assignment or review. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_projects",
    description: "List Conveyor projects available to this MCP token's user. Use a returned project id as projectId on other tools when no default project is configured or when targeting a different project.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_pull_request",
    description: "Open a GitHub pull request for a task's existing branch (the branch must already be pushed to origin). Pass projectId to target a specific project; otherwise the configured default project is used. Moves the task to ReviewPR. Returns the PR number and URL.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/pull-request.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_subtask",
    description: "Create a subtask under a parent task. Pass projectId to target a specific project; otherwise the configured default project is used. Subtasks break a larger task into independently buildable pieces. For children that instead ship on the parent's own branch/PR (e.g. per-theme tracking cards for one bundled PR), set followParentStatus so their status rides the parent's automatically.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "delete_subtask",
    description: "Delete a subtask by ID. Pass projectId to target a specific project; otherwise the configured default project is used. This is permanent \u2014 use update_subtask to set status to Cancelled if you only want to close it.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_subtasks",
    description: "List all subtasks of a parent task with their status and ordering. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_subtask",
    description: "Update a subtask's fields: title, description, plan, status, ordering, or story points. Pass projectId to target a specific project; otherwise the configured default project is used. Moving a subtask beyond Planning auto-fills missing story points and agent assignment \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_suggestion",
    description: "Suggest a feature, improvement, rule, or idea for the project. Pass projectId to target a specific project; otherwise the configured default project is used. Duplicates are deduped and your upvote is recorded.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/suggestions.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_reviewer",
    description: "Add a project member as a reviewer on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Idempotent \u2014 adding an existing reviewer is a no-op.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_and_merge_pr",
    description: "Approve and merge a child task's pull request. Pass projectId to target a specific project; otherwise the configured default project is used. Only succeeds if all CI/CD checks are passing. The child task must be in ReviewPR status with a PR.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_task",
    description: "Move a task forward in the review flow (ReviewPR -> ReviewDev, or -> Complete). Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_task",
    description: "Create a new task with title, description, and optional plan. Pass projectId to target a specific project; otherwise the configured default project is used. Icon, story points, and agent assignment are auto-filled when a task is created in (or later moved to) a status beyond Planning \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_card_by_slug",
    description: "Get full card details by the slug from a card URL (/cards/<slug>) instead of a task ID. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task",
    description: "Get full task details including plan, chat history, PR info, subtasks, and build status. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task_logs",
    description: "Read CLI execution logs from a task. Pass projectId to target a specific project; otherwise the configured default project is used. Returns agent reasoning, tool calls, setup output, and other execution events. For human chat use read_task_chat.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task_sessions",
    description: "Read compute-session state for a task: legacy CodespaceSession rows plus v3 workspaces, including purpose=review review workspaces. Shows pod identity, liveness, lifecycle, and code-review claim state. Use to diagnose stalled/dead agents or code-review runs. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_tags",
    description: "List all project tags with their names, IDs, and colors. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_tasks",
    description: "List project tasks, optionally filtered by status or assignment (a specific assignee, or unassigned tasks). Pass projectId to target a specific project; otherwise the configured default project is used. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "post_to_chat",
    description: "Post a message to a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "read_task_chat",
    description: "Read messages from a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used. For agent execution logs use get_task_logs.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_reviewer",
    description: "Remove a reviewer from a task. Pass projectId to target a specific project; otherwise the configured default project is used. Idempotent \u2014 removing a non-reviewer is a no-op.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "request_changes",
    description: "Post feedback and send task back to InProgress for more work. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "search_tasks",
    description: "Search cards by tag name, text query, status, type, and/or assignment. Defaults to type=task \u2014 pass typeFilters to include incidents/suggestions. Pass projectId to target a specific project; otherwise the configured default project is used. Use tag names like 'agent-runner', not IDs. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_task",
    description: "Update task fields: title, description, plan, status, or assignment. Pass projectId to target a specific project; otherwise the configured default project is used. Moving a task beyond Planning auto-fills any missing icon, story points, and agent assignment \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_attach_info",
    description: "Return SSH/SFTP attach metadata for a running task Claudespace, plus hosted preview URLs and configured preview ports. Optionally installs an OpenSSH public key for this attach session.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_preview_urls",
    description: "Return the hosted preview URLs and preview ports for a running task Claudespace. This mirrors the web UI preview link metadata.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_start_tunnel",
    description: "Start a local loopback tunnel through the MCP server to a running task Claudespace port. Use port 2222 for SSH/SFTP, or one of previewPorts for app access.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_stop_tunnel",
    description: "Stop a local workspace tunnel previously opened by workspace_start_tunnel.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  }
];

// src/types/agent-tool-registry.ts
function getToolsByCategory(category) {
  return MCP_TOOL_REGISTRY.filter((tool) => tool.category === category);
}

// src/constants/timezone.ts
var PROJECT_TIMEZONE = "America/Los_Angeles";

// src/constants/agent-session-runner.ts
var CODESPACE_SESSION_ACTIVE_RUNNER_STATUSES = [
  "connected",
  "idle",
  "busy",
  "running",
  "fetching_context",
  "waiting_for_input",
  "setup",
  "connecting"
];
var TERMINAL_TASK_STATUSES = ["Complete", "Cancelled"];
var AGENT_STATUS_REASON_USER_QUESTION = "user_question";

// src/constants/agent-chat-history.ts
var TASK_CHAT_HISTORY_LIMIT = 20;
var PM_CHAT_HISTORY_LIMIT = 40;
var AGENT_CHAT_HISTORY_FETCH_LIMIT = Math.max(TASK_CHAT_HISTORY_LIMIT, PM_CHAT_HISTORY_LIMIT) + 10;

// src/constants/workspace-tunnel.ts
var WORKSPACE_TUNNEL_HEARTBEAT_MS = 3e4;

// src/room-helpers.ts
function serviceRoom(service, entityId) {
  return `${service}:${entityId}`;
}
function userRoom(userId) {
  return `user:${userId}`;
}
function notificationUserRoom(userId) {
  return `notificationService:user:${userId}`;
}
function cliRoom(taskId) {
  return `task:${taskId}:cli`;
}
function projectCliRoom(projectId, userId) {
  return `project:${projectId}:cli:${userId}`;
}
function projectEventsRoom(projectId) {
  return `project:${projectId}:events`;
}

// src/utils/stat-date.ts
function todayInProjectTZ() {
  return (/* @__PURE__ */ new Date()).toLocaleDateString("en-CA", { timeZone: PROJECT_TIMEZONE });
}
function formatDateInProjectTZ(date) {
  return date.toLocaleDateString("en-CA", { timeZone: PROJECT_TIMEZONE });
}
function resolveCustomRange(startDate, endDate, today) {
  const [lo, hi] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
  return { sinceDate: lo, untilDate: hi < today ? hi : today };
}
function resolveStatDateRange(statDate, fallbackDays) {
  const today = todayInProjectTZ();
  if (statDate) {
    if (statDate.mode === "custom" && statDate.startDate && statDate.endDate) {
      return resolveCustomRange(statDate.startDate, statDate.endDate, today);
    }
    if (statDate.mode === "day" && typeof statDate.month === "number" && typeof statDate.day === "number") {
      const dayStr = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-${String(statDate.day).padStart(2, "0")}`;
      const clamped = dayStr < today ? dayStr : today;
      return { sinceDate: clamped, untilDate: clamped };
    }
    if (statDate.mode === "month" && typeof statDate.month === "number") {
      const sinceDate3 = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-01`;
      const lastDay = new Date(statDate.year, statDate.month, 0).getDate();
      const endStr2 = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { sinceDate: sinceDate3, untilDate: endStr2 < today ? endStr2 : today };
    }
    const sinceDate2 = `${statDate.year}-01-01`;
    const endStr = `${statDate.year}-12-31`;
    return { sinceDate: sinceDate2, untilDate: endStr < today ? endStr : today };
  }
  const days = fallbackDays ?? 30;
  const d = /* @__PURE__ */ new Date();
  d.setDate(d.getDate() - days);
  const sinceDate = formatDateInProjectTZ(d);
  return { sinceDate, untilDate: today };
}
function generateDateRange(sinceStr, untilStr) {
  const dates = [];
  const [sy, sm, sd] = sinceStr.split("-").map(Number);
  const [uy, um, ud] = untilStr.split("-").map(Number);
  let current = Date.UTC(sy, sm - 1, sd);
  const end = Date.UTC(uy, um - 1, ud);
  while (current <= end) {
    const d = new Date(current);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    dates.push(`${yyyy}-${mm}-${dd}`);
    current += 24 * 60 * 60 * 1e3;
  }
  return dates;
}

// src/utils/message-filter.ts
function messageMatchesFilters(msg, channelFilters) {
  if (!channelFilters || channelFilters.length === 0) return true;
  if (msg.role === "user") return true;
  const filterSet = new Set(channelFilters);
  const isToolCall = msg.metadata?.type === "activity_block";
  if (isToolCall) return filterSet.has("tools");
  return filterSet.has(msg.channel ?? "agent");
}

// src/utils/mentions.ts
var MENTION_TOKEN_REGEX = /@\[(\w+):([^\]]+)\]/g;
function serializeMention(type, id) {
  return `@[${type}:${id}]`;
}
function buildMentionKey(type, id) {
  return `${type}:${id}`;
}
function parseMentions(content) {
  const tokens = [];
  for (const match of content.matchAll(MENTION_TOKEN_REGEX)) {
    tokens.push({ type: match[1], id: match[2] });
  }
  return tokens;
}
var BUILDER_STREAM_ID = "builder";
var REVIEWER_STREAM_ID = "reviewer";
var STREAM_MENTION_IDS = /* @__PURE__ */ new Set([BUILDER_STREAM_ID, REVIEWER_STREAM_ID]);
function getStreamMentionTarget(tokens) {
  const hit = tokens.find((t) => t.type === "agent" && STREAM_MENTION_IDS.has(t.id));
  return hit ? hit.id : null;
}
function getStreamMentionTargetFromKeys(keys) {
  for (const key of keys) {
    if (key === buildMentionKey("agent", BUILDER_STREAM_ID)) return BUILDER_STREAM_ID;
    if (key === buildMentionKey("agent", REVIEWER_STREAM_ID)) return REVIEWER_STREAM_ID;
  }
  return null;
}
var STREAM_MENTION_STRIP_REGEX = /(?:@\[agent:(?:builder|reviewer)\]|@\S+ \(agent:(?:builder|reviewer)\))[ \t]*/g;
function stripStreamMentionText(content) {
  return content.replace(STREAM_MENTION_STRIP_REGEX, "").trim();
}
function resolveMentions(content, metadata) {
  if (!metadata || typeof metadata !== "object") return content;
  const meta = metadata;
  if (meta.type !== "mentions" || !meta.mentions) return content;
  const mentions = meta.mentions;
  return content.replace(MENTION_TOKEN_REGEX, (_match, type, id) => {
    const key = `${type}:${id}`;
    const mention = mentions[key];
    if (!mention) return _match;
    const prefix = mention.slug ? `#${mention.slug}` : `@${mention.label}`;
    return `${prefix} (${type}:${id})`;
  });
}

// src/utils/autopilot-ratio.ts
var AUTOPILOT_RATIO_MIN = 1;
var AUTOPILOT_RATIO_MAX = 6.5;
function computeMemoryBounds(agentCpu, sidecarCpu, sidecarMemoryGi) {
  const totalCpu = agentCpu + sidecarCpu;
  const minPodMemory = totalCpu * AUTOPILOT_RATIO_MIN;
  const maxPodMemory = totalCpu * AUTOPILOT_RATIO_MAX;
  const minAgentMemory = minPodMemory - sidecarMemoryGi;
  const maxAgentMemory = maxPodMemory - sidecarMemoryGi;
  const minMemoryGi = Math.max(1, Math.ceil(minAgentMemory));
  const maxMemoryGi = Math.floor(maxAgentMemory);
  return { minMemoryGi, maxMemoryGi };
}
function computePodRatio(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi) {
  const totalCpu = agentCpu + sidecarCpu;
  const totalMemory = agentMemoryGi + sidecarMemoryGi;
  if (totalCpu === 0) return 0;
  return totalMemory / totalCpu;
}
function isValidAutopilotConfig(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi) {
  const ratio = computePodRatio(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi);
  return ratio >= AUTOPILOT_RATIO_MIN && ratio <= AUTOPILOT_RATIO_MAX;
}

// src/utils/sidecar-overhead.ts
var SIDECAR_OVERHEAD = {
  postgresql: {
    // 500m — un-starve CPU fix; keep in sync with SIDECAR_SPECS.postgresql
    cpu: 0.5,
    // 128Mi
    memoryGi: 0.125
  },
  redis: {
    // 25m
    cpu: 0.025,
    // 32Mi
    memoryGi: 0.03125
  },
  elasticsearch: {
    // 100m
    cpu: 0.1,
    // 256Mi
    memoryGi: 0.25
  },
  lgtm: {
    // 250m — bundles Grafana + Loki + Tempo + Mimir + OTEL Collector
    cpu: 0.25,
    // 1Gi
    memoryGi: 1
  },
  "firebase-auth-emulator": {
    // 100m
    cpu: 0.1,
    // 256Mi
    memoryGi: 0.25
  }
};
function sidecarRequestTotals(deps) {
  let totalCpu = 0;
  let totalMemoryGi = 0;
  for (const dep of deps) {
    const overhead = SIDECAR_OVERHEAD[dep];
    if (overhead) {
      totalCpu += overhead.cpu;
      totalMemoryGi += overhead.memoryGi;
    }
  }
  return { cpu: totalCpu, memoryGi: totalMemoryGi };
}

// src/utils/xp.ts
var SP_XP_RATE = 10;
var REPORT_XP_RATE = 10;
var REVIEW_XP_RATE = 5;
var MAX_LEVEL = 99;
function xpToReachLevel(level) {
  if (level <= 1) return 0;
  return 5 * level * (level - 1);
}
function computeTotalXp({ spValueSum, reportCount, reviewCount }) {
  const spXp = spValueSum * SP_XP_RATE;
  const reportXp = reportCount * REPORT_XP_RATE;
  const reviewXp = reviewCount * REVIEW_XP_RATE;
  return { spXp, reportXp, reviewXp, totalXp: spXp + reportXp + reviewXp };
}
function levelFromXp(totalXp) {
  const xp = Number.isFinite(totalXp) && totalXp > 0 ? Math.floor(totalXp) : 0;
  let level = Math.floor((5 + Math.sqrt(25 + 20 * xp)) / 10);
  while (xpToReachLevel(level + 1) <= xp) level++;
  while (level > 1 && xpToReachLevel(level) > xp) level--;
  level = Math.min(MAX_LEVEL, Math.max(1, level));
  if (level >= MAX_LEVEL) {
    return {
      level: MAX_LEVEL,
      xpIntoLevel: xp - xpToReachLevel(MAX_LEVEL),
      xpForNextLevel: 0,
      progress: 1
    };
  }
  const floor = xpToReachLevel(level);
  const xpForNextLevel = xpToReachLevel(level + 1) - floor;
  const xpIntoLevel = xp - floor;
  const progress = xpForNextLevel === 0 ? 1 : Math.min(1, Math.max(0, xpIntoLevel / xpForNextLevel));
  return { level, xpIntoLevel, xpForNextLevel, progress };
}

// src/utils/format-number.ts
var RAW_MAX = 9999.5;
var K_MAX = 999950;
var M_MAX = 99995e4;
function scaled(abs, divisor, suffix) {
  return `${(abs / divisor).toFixed(1).replace(/\.0$/, "")}${suffix}`;
}
function formatCompactNumber(value) {
  if (!Number.isFinite(value)) return "0";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs < RAW_MAX) return `${sign}${Math.round(abs)}`;
  if (abs < K_MAX) return `${sign}${scaled(abs, 1e3, "K")}`;
  if (abs < M_MAX) return `${sign}${scaled(abs, 1e6, "M")}`;
  return `${sign}${scaled(abs, 1e9, "B")}`;
}
function formatCompactSigned(value) {
  const formatted = formatCompactNumber(value);
  return value > 0 ? `+${formatted}` : formatted;
}
function formatCompactUsd(value) {
  if (!Number.isFinite(value)) return "$0";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs < RAW_MAX) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${formatCompactNumber(abs)}`;
}

// src/utils/pod-size-presets.ts
var POD_SIZE_NAMES = ["small", "medium", "large"];
var POD_SIZE_PRESETS = {
  small: { cpu: 1.95, memory: 7 },
  medium: { cpu: 3.9, memory: 14 },
  large: { cpu: 5.25, memory: 18 }
};
var DEFAULT_POD_SIZE = "small";
function isPodSizeName(value) {
  return typeof value === "string" && POD_SIZE_NAMES.includes(value);
}

// src/utils/task-planning.ts
var PRE_BUILD_TASK_STATUSES = /* @__PURE__ */ new Set(["Planning", "Open"]);
function hasTaskPlan(plan) {
  return !!plan?.trim();
}

// src/utils/gcp-exceptional.ts
var EXCEPTIONAL_GCP_LOG_FILTER = "(severity>=WARNING OR httpRequest.status>=400)";
function isExceptionalLogEntry(entry) {
  return ["WARNING", "ERROR", "CRITICAL", "ALERT", "EMERGENCY"].includes(entry.severity) || (entry.httpRequest?.status ?? 0) >= 400;
}
function exceptionalLogKind(entry) {
  return ["ERROR", "CRITICAL", "ALERT", "EMERGENCY"].includes(entry.severity) || (entry.httpRequest?.status ?? 0) >= 500 ? "error" : "warning";
}
function normalize(value) {
  return value.replace(/(?<=\btrace=)[0-9a-f]+/gi, ":id").replace(/[0-9a-f]{8,}/gi, ":id").replace(/\b\d+\b/g, ":n").replace(/\/\d+(?=\/|$)/g, "/:id").trim().toLowerCase();
}
function exceptionalLogSource(entry) {
  return entry.resource.service_name ?? entry.resource.pod_name ?? entry.resource.database_id ?? entry.resourceType ?? "unknown";
}
function normalizedSearchTerms(entry) {
  return normalize(entry.httpRequest?.url ?? entry.message).replace(/:[a-z]+/g, " ").replace(/[^a-z0-9/_-]+/g, " ").trim().replace(/\s+/g, " ");
}
function representativeFilter(entry) {
  const http = entry.httpRequest;
  const stableTerms = normalizedSearchTerms(entry).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const searchFilter = stableTerms ? `SEARCH("${stableTerms}")` : void 0;
  if (http && (http.status ?? 0) >= 400) {
    const method = http.method?.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return [
      `httpRequest.status=${http.status}`,
      method ? `httpRequest.requestMethod="${method}"` : void 0,
      searchFilter
    ].filter(Boolean).join(" AND ");
  }
  return [`severity=${entry.severity}`, searchFilter].filter(Boolean).join(" AND ");
}
function exceptionalLogSignature(entry) {
  const source = exceptionalLogSource(entry);
  const http = entry.httpRequest;
  return [
    source,
    exceptionalLogKind(entry),
    http?.method ?? "",
    http?.status ?? "",
    normalize(http?.url ?? ""),
    normalize(entry.message)
  ].join("|");
}
function groupExceptionalLogEntries(entries, options) {
  const grouped = /* @__PURE__ */ new Map();
  let errors = 0;
  let warnings = 0;
  for (const entry of entries) {
    if (!isExceptionalLogEntry(entry)) continue;
    const kind = exceptionalLogKind(entry);
    if (kind === "error") errors += 1;
    else warnings += 1;
    const signature = exceptionalLogSignature(entry);
    const existing = grouped.get(signature);
    if (existing) {
      existing.count += 1;
      if (entry.timestamp < existing.firstSeen) existing.firstSeen = entry.timestamp;
      if (entry.timestamp > existing.lastSeen) existing.lastSeen = entry.timestamp;
      existing.samples.push(entry);
      continue;
    }
    grouped.set(signature, {
      id: signature,
      env: options.env,
      source: exceptionalLogSource(entry),
      kind,
      count: 1,
      firstSeen: entry.timestamp,
      lastSeen: entry.timestamp,
      summary: entry.message,
      representativeFilter: representativeFilter(entry),
      samples: [entry]
    });
  }
  const groups = [...grouped.values()];
  for (const group of groups) {
    group.samples.sort((left, right) => right.timestamp.localeCompare(left.timestamp));
    group.samples = group.samples.slice(0, options.sampleLimit);
  }
  groups.sort(
    (left, right) => (left.kind === right.kind ? 0 : left.kind === "error" ? -1 : 1) || right.lastSeen.localeCompare(left.lastSeen) || right.count - left.count
  );
  return { errors, warnings, groups };
}
export {
  ACTIVE_WORK_STATUSES,
  AGENT_CHAT_HISTORY_FETCH_LIMIT,
  AGENT_KEY_KINDS,
  AGENT_PROVIDERS,
  AGENT_STATUS_REASON_USER_QUESTION,
  ALLOWED_FILE_MIME_TYPES,
  AUTOPILOT_RATIO_MAX,
  AUTOPILOT_RATIO_MIN,
  AddDependencyRequestSchema,
  AddProjectTaskDependencyRequestSchema,
  AddProjectTaskReviewerRequestSchema,
  AgentEventSchema,
  AgentHeartbeatSchema,
  AgentQuestionOptionSchema,
  AgentQuestionSchema,
  AnswerAgentQuestionRequestSchema,
  ApproveAndMergePRRequestSchema,
  ApproveManualTestRequestSchema,
  ApproveProjectManualTestRequestSchema,
  ApproveProjectMergePRRequestSchema,
  AskUserQuestionRequestSchema,
  BUILDER_STREAM_ID,
  CARD_TYPE_STATUSES,
  CARD_TYPE_VALUES,
  CODESPACE_SESSION_ACTIVE_RUNNER_STATUSES,
  CREATED_DESC_STATUSES,
  CRITICAL_AUTOMATED_SOURCES,
  CancelTaskQueuedMessageRequestSchema,
  ClearAgentTodosRequestSchema,
  ConfirmFileUploadRequestSchema,
  ConfirmProjectFileUploadRequestSchema,
  ConnectAgentRequestSchema,
  CreateFollowUpTaskRequestSchema,
  CreatePRInputSchema,
  CreatePRResponseSchema,
  CreateProjectPullRequestRequestSchema,
  CreateProjectReleaseRequestSchema,
  CreateProjectSubtaskRequestSchema,
  CreateProjectSuggestionRequestSchema,
  CreateProjectTagRequestSchema,
  CreateProjectTaskRequestSchema,
  CreatePullRequestRequestSchema,
  CreateSubtaskRequestSchema,
  CreateSuggestionRequestSchema,
  CycleCodingAgentKeyRequestSchema,
  DEFAULT_HAIKU_MODEL,
  DEFAULT_OPUS_MODEL,
  DEFAULT_POD_SIZE,
  DEFAULT_SONNET_MODEL,
  DEPLOYMENT_TARGET_ENVIRONMENTS,
  DEPLOYMENT_TARGET_PROVIDERS,
  DeleteProjectSubtaskRequestSchema,
  DeleteSubtaskRequestSchema,
  DeleteSubtaskResponseSchema,
  DeleteTaskAuditRequestSchema,
  DiscoveredPortSchema,
  EMBEDDABLE_IMAGE_TYPES,
  EXCEPTIONAL_GCP_LOG_FILTER,
  EditManualTestRequestSchema,
  EditProjectManualTestRequestSchema,
  EmitAgentEventRequestSchema,
  EndReviewSessionRequestSchema,
  FABLE_MODEL,
  FlushSingleQueuedMessageRequestSchema,
  FlushTaskQueueRequestSchema,
  GCP_ENVS,
  GenerateTaskIconRequestSchema,
  GetActiveAuditSessionsRequestSchema,
  GetActivePtySessionRequestSchema,
  GetAgentStatusRequestSchema,
  GetChatMessagesRequestSchema,
  GetCliHistoryRequestSchema,
  GetDependenciesRequestSchema,
  GetProjectAttachmentRequestSchema,
  GetProjectAvailableTuisRequestSchema,
  GetProjectSummaryRequestSchema,
  GetProjectTaskChatRequestSchema,
  GetProjectTaskCliRequestSchema,
  GetProjectTaskDependenciesRequestSchema,
  GetProjectTaskRequestSchema,
  GetProjectTaskSessionsRequestSchema,
  GetSuggestionsRequestSchema,
  GetTaskAuditAggregatesRequestSchema,
  GetTaskAuditRequestSchema,
  GetTaskAuditsRequestSchema,
  GetTaskContextRequestSchema,
  GetTaskFileRequestSchema,
  GetTaskFilesRequestSchema,
  GetTaskPropertiesRequestSchema,
  GetTaskRequestSchema,
  GetUiCliHistoryRequestSchema,
  HeartbeatRequestSchema,
  HeartbeatResponseSchema,
  IDLE_HEARTBEAT_MS,
  KnownAgentEventSchema,
  ListAccessibleProjectsRequestSchema,
  ListActivePtySessionsRequestSchema,
  ListIconsRequestSchema,
  ListManualTestsRequestSchema,
  ListMyLiveSessionsRequestSchema,
  ListProjectManualTestsRequestSchema,
  ListProjectMembersRequestSchema,
  ListProjectSessionGroupsRequestSchema,
  ListProjectSubtasksRequestSchema,
  ListProjectTagsRequestSchema,
  ListProjectTaskFilesRequestSchema,
  ListProjectTasksRequestSchema,
  ListSubtasksRequestSchema,
  MAX_AGENT_FLAVOR_LENGTH,
  MAX_DISCOVERED_PORTS,
  MAX_FILE_SIZE_BYTES,
  MAX_LEVEL,
  MCP_TOOL_REGISTRY,
  MENTION_TOKEN_REGEX,
  MarkInitialPromptSubmittedRequestSchema,
  NotifyAgentVersionRequestSchema,
  PM_CHAT_HISTORY_LIMIT,
  POD_SIZE_NAMES,
  POD_SIZE_PRESETS,
  PREVIEW_PORT_DENY_LIST,
  PREVIOUS_SONNET_MODEL,
  PRE_BUILD_TASK_STATUSES,
  PROJECT_FUNCTION_DEFAULTS,
  PROJECT_TIMEZONE,
  PickFaIconRequestSchema,
  PostChildChatMessageRequestSchema,
  PostToChatInputSchema,
  PostToChatRequestSchema,
  PostToChatResponseSchema,
  PostToProjectChatRequestSchema,
  PostToProjectTaskChatRequestSchema,
  ProjectTagContextPathSchema,
  PtyAttachRequestSchema,
  PtyChatAttachRequestSchema,
  PtyChatEventPayloadSchema,
  PtyChatEventRequestSchema,
  PtyEndedRequestSchema,
  PtyInputRequestSchema,
  PtyOutputRequestSchema,
  PtyResizeRequestSchema,
  QueryManualTestsRequestSchema,
  QueryProjectGcpLogsRequestSchema,
  QueryProjectManualTestsRequestSchema,
  REPORT_XP_RATE,
  REVIEWER_STREAM_ID,
  REVIEW_XP_RATE,
  RefreshGithubTokenRequestSchema,
  RefreshGithubTokenResponseSchema,
  RejectManualTestRequestSchema,
  RejectProjectManualTestRequestSchema,
  RemoveDependencyRequestSchema,
  RemoveManualTestRequestSchema,
  RemoveProjectManualTestRequestSchema,
  RemoveProjectTaskDependencyRequestSchema,
  RemoveProjectTaskReviewerRequestSchema,
  ReportAgentStatusRequestSchema,
  ReportDiscoveredPortsRequestSchema,
  ReportReviewSpawnFailureRequestSchema,
  ReportSessionSpawnFailureRequestSchema,
  ReportTaskAuditResultRequestSchema,
  RequestFileUploadRequestSchema,
  RequestProjectFileUploadRequestSchema,
  ResumeAdhocSessionRequestSchema,
  SIDECAR_OVERHEAD,
  SP_XP_RATE,
  STRANDED_RUNNER_STATUSES,
  SearchFaIconsRequestSchema,
  SearchProjectTasksRequestSchema,
  SendSoftStopRequestSchema,
  SessionStartRequestSchema,
  SessionStartResponseSchema,
  SessionStopRequestSchema,
  SessionStopResponseSchema,
  SetManualTestsRequestSchema,
  SetProjectManualTestsRequestSchema,
  SpawnTaskReviewRequestSchema,
  SpawnTaskSessionRequestSchema,
  StartAdhocSessionRequestSchema,
  StartChildCloudBuildRequestSchema,
  StartProjectBuildRequestSchema,
  StartProjectWorkspaceRequestSchema,
  StartTagAuditRequestSchema,
  StartTaskAuditRequestSchema,
  StopAdhocSessionRequestSchema,
  StopChildBuildRequestSchema,
  StopProjectBuildRequestSchema,
  StopProjectWorkspaceRequestSchema,
  StopTaskSessionRequestSchema,
  StoreSessionIdRequestSchema,
  StoreSessionIdResponseSchema,
  SubmitCodeReviewResultRequestSchema,
  TASK_CHAT_HISTORY_LIMIT,
  TASK_LESS_WORKSPACE_PURPOSES,
  TASK_STATUS_ORDER,
  TERMINAL_TASK_STATUSES,
  TUI_KINDS,
  TriggerIdentificationRequestSchema,
  UpdateChildStatusRequestSchema,
  UpdateProjectSubtaskRequestSchema,
  UpdateProjectTagRequestSchema,
  UpdateProjectTaskRequestSchema,
  UpdateSubtaskRequestSchema,
  UpdateTaskFieldsRequestSchema,
  UpdateTaskPropertiesRequestSchema,
  UpdateTaskStatusRequestSchema,
  UpdateTaskStatusResponseSchema,
  VoteProjectSuggestionRequestSchema,
  VoteSuggestionRequestSchema,
  WORKSPACE_DESIRED_STATES,
  WORKSPACE_OBSERVED_STATES,
  WORKSPACE_POD_PHASES,
  WORKSPACE_PURPOSES,
  WORKSPACE_SESSION_MODES,
  WORKSPACE_SESSION_ROLES,
  WORKSPACE_SESSION_STATUSES,
  WORKSPACE_TUNNEL_HEARTBEAT_MS,
  applyAgentFlavor,
  buildMentionKey,
  cliRoom,
  computeMemoryBounds,
  computePodRatio,
  computeTotalXp,
  createAgentSessionRoom,
  createProjectRoom,
  deriveConnectionState,
  exceptionalLogKind,
  exceptionalLogSignature,
  formatCompactNumber,
  formatCompactSigned,
  formatCompactUsd,
  formatHeartbeatAge,
  generateDateRange,
  getStreamMentionTarget,
  getStreamMentionTargetFromKeys,
  getToolsByCategory,
  groupExceptionalLogEntries,
  hasTaskPlan,
  isActiveWorkStatus,
  isAllowablePreviewPort,
  isCreatedDescStatus,
  isCuid,
  isEmbeddableFile,
  isExceptionalLogEntry,
  isPodSizeName,
  isValidAutopilotConfig,
  levelFromXp,
  messageMatchesFilters,
  notificationUserRoom,
  parseMentions,
  projectCliRoom,
  projectEventsRoom,
  resolveAllowedPreviewPorts,
  resolveCodeReviewMode,
  resolveGcpEnvResources,
  resolveMentions,
  resolveProjectFunctionConfig,
  resolveStatDateRange,
  sanitizeDiscoveredPorts,
  sanitizeSessionPreviewPorts,
  serializeMention,
  serviceRoom,
  sidecarRequestTotals,
  stripStreamMentionText,
  todayInProjectTZ,
  userRoom,
  validateStatusForType,
  xpToReachLevel
};
